import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Button, Container, Group } from '@mantine/core';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import MapView from '@arcgis/core/views/MapView';
import DistanceMeasurement2D from '@arcgis/core/widgets/DistanceMeasurement2D';
import AreaMeasurement2D from '@arcgis/core/widgets/AreaMeasurement2D';
import { errorBoundaryWrapper, useAgLazyQuery, useAsyncEffect, useLocalStorageState } from '@repo/utils';
import ArcGISMap from '@arcgis/core/Map';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import LayerList from '@arcgis/core/widgets/LayerList';
import ScaleBar from '@arcgis/core/widgets/ScaleBar';
import Expand from '@arcgis/core/widgets/Expand';
import Legend from '@arcgis/core/widgets/Legend';
import { AIMMapServer, USAExtent } from '@repo/map';
import Extent from '@arcgis/core/geometry/Extent';
import { Opportunity, useGetOpportunitiesQuery, useLazyGetOpportunityQuery } from '@repo/services';

const PopupMap = ({ opportunityId }: { opportunityId?: string }) => {
  const popupRef = useRef(null);
  const toolsRef = useRef(null);
  const { data: opportunities = [], isLoading: opportunityDataLoading } = useGetOpportunitiesQuery(null);
  const [opportunityExtent, setOpportunityExtent] = useLocalStorageState(
    `opportunityExtent-${opportunityId}`,
    { defaultValue: USAExtent } as any
  );
  const [opportunity] = useAgLazyQuery<Opportunity>(
    opportunityId && parseFloat(opportunityId) !== 0 ? opportunityId : undefined,
    useLazyGetOpportunityQuery
  );
  const [featureLayer, setFeatureLayer] = useState<FeatureLayer>();
  const [view, setView] = useState<MapView>();
  const [activeMeasureTool, setActiveMeasureTool] = useState<
    DistanceMeasurement2D | AreaMeasurement2D | null
  >(null);
  // const pinLayer = useFeatureLayer({ map, view });
  // useDebounceEffect(() => {
  //   if (!featureLayer && view && opportunity) {
  //     view.when(async () => {
  //       const fs = await getFeatureLayerByLayerName({
  //         ...EditAimMapViewServer,
  //         definitionExpression: `OpportunityId=${opportunityId}`,
  //         // definitionExpression: `JARVISID='PA-AQ-1333'`,
  //         layerName: 'Potential Acquisitions',
  //         title: opportunity.title,
  //         labelsVisible: false,
  //         maxScale: 0,
  //         minScale: 300000000
  //       });
  //       const { count, extent } = await fs.queryExtent();
  //       view.map.add(fs);
  //       // fs.on('layerview-create', async (event) => {
  //       //   const layer = event.layerView.layer as FeatureLayer;
  //       //   if (count > 0) {
  //       //     const zoomExtent = extent.expand(3);
  //       //     setOpportunityExtent(zoomExtent);
  //       //     await view.goTo(zoomExtent);
  //       //   }
  //       // });
  //       setFeatureLayer(fs);
  //     });
  //   }
  //   return () => {
  //     if (featureLayer && view) view.map.remove(featureLayer);
  //   };
  // }, [featureLayer, view, opportunity]);

  useAsyncEffect(async () => {
    if (featureLayer && view && opportunityId) {
      featureLayer.definitionExpression = `OpportunityId=${opportunityId}`;
      const { count, extent } = await featureLayer.queryExtent();
      let zoomExtent = new Extent(USAExtent);
      if (count > 0) {
        zoomExtent = extent.expand(3);
      }
      setOpportunityExtent(zoomExtent.toJSON());
      await view.goTo(zoomExtent);
    }
  }, [opportunityId, featureLayer, view]);

  useEffect(() => {
    let mapView: MapView | undefined;
    if (popupRef.current && toolsRef.current) {
      const map = new ArcGISMap({ basemap: 'hybrid' });
      mapView = new MapView({ container: popupRef.current, map, extent: opportunityExtent } as any);
      const mapImageLayer = new MapImageLayer({
        url: AIMMapServer.Acquisitions.url,
        title: 'Operations'
      });
      const potentialAcquisitionsLayer = new MapImageLayer({
        url: AIMMapServer.Acquisitions.url,
        definitionExpression: '1=1',
        title: 'Other Potential Acquisitions'
      } as any);
      map.add(mapImageLayer);
      map.add(potentialAcquisitionsLayer);
      const ll = new LayerList({
        view: mapView
      });

      const legend = new Legend({ view: mapView });
      const expand = new Expand({ view, content: legend });
      const expandLayerList = new Expand({ view, content: ll });
      mapView.ui.add(expand, 'bottom-right');
      // });
      const scaleBar = new ScaleBar({
        view: mapView,
        unit: 'dual' // The scale bar displays both metric and non-metric units.
      });
      mapView.ui.add(scaleBar, 'bottom-left');

      mapView.ui.add(toolsRef.current, 'top-right');
      mapView.ui.add(expandLayerList, 'top-right');
      setView(mapView);
    }
    return () => {
      if (mapView) {
        mapView.destroy();
      }
    };
  // }, [popupRef.current, toolsRef.current, opportunity]);
}, [popupRef.current, toolsRef.current]);

  const zoomToFeatureLayerExtent = async () => {
    const { extent }: Extent = await featureLayer?.queryExtent();
    setOpportunityExtent(extent?.toJSON());
    await view?.goTo(extent?.expand(2.2));
  };

  function setActiveWidget(type: string) {
    const toggledOff = activeMeasureTool?.id === type;
    if (activeMeasureTool) {
      activeMeasureTool.destroy();
      view?.ui.remove(activeMeasureTool);
      setActiveMeasureTool(null);
    }
    if (type === 'distance' && view && !toggledOff) {
      const newTool = new DistanceMeasurement2D({ view, id: type, unit: 'miles' });
      setActiveMeasureTool(newTool);
      view.ui.add(newTool, 'bottom-right');
      newTool.viewModel.start();
    }
    if (type === 'area' && view && !toggledOff) {
      const newTool = new AreaMeasurement2D({ view, id: type, unit: 'acres' });
      setActiveMeasureTool(newTool);
      view.ui.add(newTool, 'bottom-right');
      newTool.viewModel.start();
    }
  }

  return (
    // <div style={{ width: '100%', height: '100vh' }}>
    //   <div style={{ width: '100%', height: '100%' }} ref={popupRef}></div>
    //   <div ref={toolsRef} style={{ background: 'rgb(99, 141, 165)', padding: 5 }}>
    //     <Group>
    //       <Button
    //         size='large'
    //         //@ts-ignore
    //         icon={<i className={`esri-icon-zoom-to-object`} />}
    //         onClick={() => zoomToFeatureLayerExtent()}
    //       />
    //       <Button
    //         size='large'
    //         //@ts-ignore
    //         type={activeMeasureTool?.id === 'distance' ? 'primary' : 'default'}
    //         icon={<i className={`esri-icon-measure-line large`} />}
    //         id='distanceButton'
    //         title='Measure distance between two or more points'
    //         onClick={() => setActiveWidget('distance')}
    //       />
    //       <Button
    //         size='large'
    //         //@ts-ignore
    //         type={activeMeasureTool?.id === 'area' ? 'primary' : 'default'}
    //         icon={<i className='esri-icon-measure-area large' />}
    //         id='areaButton'
    //         onClick={() => setActiveWidget('area')}
    //         title='Measure area'
    //       />
    //     </Group>
    //   </div>
    // </div>
    <Container fluid style={{ width: '100%', height: '100vh', padding: 0 }}>
    <div style={{ width: '100%', height: '100%' }} ref={popupRef}></div>
    <div ref={toolsRef} style={{ background: 'rgb(99, 141, 165)', padding: '10px', borderRadius: '8px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)' }}>
      <Group gap={'sm'}>
        <Button
          size="lg"
          variant="filled"
          color="blue"
          leftSection={<i className="esri-icon-zoom-to-object" />}
          onClick={() => zoomToFeatureLayerExtent()}
        >
          Zoom
        </Button>
        <Button
          size="lg"
          variant={activeMeasureTool?.id === 'distance' ? 'filled' : 'outline'}
          color={activeMeasureTool?.id === 'distance' ? 'blue' : 'white'}
          leftSection={<i className="esri-icon-measure-line large" />}
          id="distanceButton"
          title="Measure distance between two or more points"
          onClick={() => setActiveWidget('distance')}
        >
          Measure Distance
        </Button>
        <Button
          size="lg"
          variant={activeMeasureTool?.id === 'area' ? 'filled' : 'outline'}
          color={activeMeasureTool?.id === 'area' ? 'blue' : 'white'}
          leftSection={<i className="esri-icon-measure-area large" />}
          id="areaButton"
          title="Measure area"
          onClick={() => setActiveWidget('area')}
        >
          Measure Area
        </Button>
      </Group>
    </div>
  </Container>
  );
};

export default errorBoundaryWrapper(PopupMap);
